<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import draggable from 'vuedraggable';
import TasksAPI from 'dashboard/api/tasks';
import TaskModal from 'dashboard/modules/tasks/components/TaskModal.vue';
import TaskForm from 'dashboard/modules/tasks/components/TaskForm.vue';
import TaskListItem from 'dashboard/modules/tasks/components/TaskListItem.vue';
import { useTaskActions } from 'dashboard/modules/tasks/composables/useTaskActions';

const props = defineProps({
  contactId: { type: [Number, String], required: true },
  // display_id da conversa (é o `id` que o front conhece)
  conversationId: { type: [Number, String], default: null },
});

const { t } = useI18n();
const { updateTask } = useTaskActions();

const tasks = ref([]);
const localTasks = ref([]);
const dragging = ref(false);
const isLoading = ref(false);
const selectedTaskId = ref(null);
const showNewTaskForm = ref(false);

async function fetchTasks() {
  if (!props.contactId) return;
  isLoading.value = true;
  try {
    const { data } = await TasksAPI.list({ contact_id: props.contactId });
    tasks.value = data.data ?? [];
    if (!dragging.value) localTasks.value = [...tasks.value];
  } catch {
    tasks.value = [];
    localTasks.value = [];
  } finally {
    isLoading.value = false;
  }
}

async function handleComplete(task) {
  const newStatus = task.status === 'completed' ? 'pending' : 'completed';
  await updateTask({
    taskId: task.id,
    status: newStatus,
    completed_at: newStatus === 'completed' ? new Date().toISOString() : null,
  });
  await fetchTasks();
}

function handleOpen(task) {
  selectedTaskId.value = task.id;
}

function onTaskCreated() {
  showNewTaskForm.value = false;
  fetchTasks();
}

async function onDragEnd() {
  dragging.value = false;
  await Promise.all(
    localTasks.value.map((task, index) =>
      TasksAPI.update(task.id, { position: index })
    )
  );
}

const taskPrefill = computed(() => ({
  ...(props.contactId ? { contact_id: Number(props.contactId) } : {}),
  ...(props.conversationId
    ? { conversation_display_id: Number(props.conversationId) }
    : {}),
}));

onMounted(fetchTasks);
watch(() => props.contactId, fetchTasks);
</script>

<template>
  <div>
    <div class="flex justify-end mb-2 relative">
      <button
        class="text-xs flex items-center gap-1 text-n-brand hover:brightness-110"
        @click="showNewTaskForm = !showNewTaskForm"
      >
        <span class="i-lucide-plus size-3.5" />
        {{ t('TASKS.CONTACT_SECTION.ADD') }}
      </button>
      <div v-if="showNewTaskForm" class="absolute right-0 top-6 z-50">
        <TaskForm
          :prefill="taskPrefill"
          @created="onTaskCreated"
          @cancel="showNewTaskForm = false"
        />
      </div>
    </div>

    <div v-if="isLoading" class="text-xs text-n-slate-10 py-2">
      {{ t('TASKS.LOADING') }}
    </div>

    <div
      v-else-if="localTasks.length === 0"
      class="text-xs text-n-slate-10 py-2"
    >
      {{ t('TASKS.CONTACT_SECTION.EMPTY') }}
    </div>

    <!-- mesma linha usada no painel, no card e no contato -->
    <draggable
      v-else
      v-model="localTasks"
      item-key="id"
      handle=".task-drag-item"
      ghost-class="opacity-40"
      class="border border-n-weak rounded-lg bg-n-surface-1 overflow-hidden"
      @start="dragging = true"
      @end="onDragEnd"
    >
      <template #item="{ element: task }">
        <TaskListItem
          :task="task"
          stacked
          hide-tags
          compact-priority
          hide-completed-info
          @open="handleOpen"
          @complete="handleComplete"
          @updated="fetchTasks"
        />
      </template>
    </draggable>

    <TaskModal
      v-if="selectedTaskId"
      :task-id="selectedTaskId"
      @close="selectedTaskId = null"
      @updated="fetchTasks"
      @deleted="
        selectedTaskId = null;
        fetchTasks();
      "
    />
  </div>
</template>
